<script setup lang="ts">
import { useForm, usePage } from "@inertiajs/vue3"
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import route from "ziggy-js"
import { Clock } from "lucide-vue-next"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/toast/use-toast"
import AppLayout from "@/layouts/AppLayout.vue"

defineOptions({
    layout: [AppLayout],
})

type Employee = {
    id: number
    employee_number: string
    name: string
    title: string
}

type Attendance = {
    status: "timed_in" | "timed_out" | "none"
    timed_in_label: string | null
    timed_out_label: string | null
}

type Punch = {
    id: number
    employee_number: string | null
    name: string | null
    title: string | null
    timed_in_label: string | null
    timed_out_label: string | null
    status: string
}

type Props = {
    dateLabel: string
    employee: Employee | null
    attendance: Attendance | null
    punches: Punch[]
}

const props = defineProps<Props>()
const page = usePage()
const { toast } = useToast()
const clockLabel = ref("")
let timer: ReturnType<typeof setInterval> | null = null

const form = useForm({
    employee_number: props.employee?.employee_number ?? "",
})

watch(
    () => props.employee?.employee_number,
    (number) => {
        if (number && !form.employee_number) {
            form.employee_number = number
        }
    }
)

watch(
    () => (page.props.flash as { status?: string } | undefined)?.status,
    (status) => {
        if (status) {
            toast({ description: status })
        }
    },
    { immediate: true }
)

function tick() {
    clockLabel.value = new Intl.DateTimeFormat("en-PH", {
        timeZone: "Asia/Manila",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    }).format(new Date())
}

onMounted(() => {
    tick()
    timer = setInterval(tick, 1000)
})

onBeforeUnmount(() => {
    if (timer) {
        clearInterval(timer)
    }
})

const statusLabel = computed(() => {
    if (props.attendance?.status === "timed_out") {
        return `Timed out at ${props.attendance.timed_out_label}`
    }

    if (props.attendance?.status === "timed_in") {
        return `Timed in at ${props.attendance.timed_in_label}`
    }

    return "Not timed in yet"
})

function clockIn() {
    form.post(route("time-clock.in"), { preserveScroll: true })
}

function clockOut() {
    form.post(route("time-clock.out"), { preserveScroll: true })
}
</script>

<template>
    <div class="min-h-screen bg-[#f3f1ea] -mx-4 sm:-mx-6 lg:-mx-8">
        <div class="bg-[rgb(181_115_5)] text-white">
            <div class="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
                <p class="text-sm font-medium uppercase tracking-[0.2em] text-white/70">Attendance</p>
                <h1 class="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Time in / Time out</h1>
                <p class="mt-2 text-white/80">{{ dateLabel }}</p>
            </div>
        </div>

        <div class="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
            <Card>
                <CardHeader class="text-center">
                    <div class="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(181_115_5)]/15 text-[rgb(145_90_0)]">
                        <Clock class="h-6 w-6" />
                    </div>
                    <CardTitle class="font-mono text-4xl tracking-tight sm:text-5xl">{{ clockLabel }}</CardTitle>
                    <CardDescription>{{ employee ? `${employee.name} · ${statusLabel}` : statusLabel }}</CardDescription>
                </CardHeader>
                <CardContent class="space-y-4">
                    <div class="space-y-2">
                        <Label for="employee_number">Employee number</Label>
                        <Input
                            id="employee_number"
                            v-model="form.employee_number"
                            class="h-12 text-center font-mono text-lg tracking-[0.2em] uppercase"
                            placeholder="AC00001"
                            autocomplete="off"
                            autocapitalize="characters"
                        />
                        <p v-if="form.errors.employee_number" class="text-sm text-destructive">
                            {{ form.errors.employee_number }}
                        </p>
                    </div>

                    <div class="grid grid-cols-2 gap-3">
                        <Button
                            type="button"
                            class="h-12 bg-[rgb(181_115_5)] text-base hover:bg-[rgb(145_90_0)]"
                            :disabled="form.processing"
                            @click="clockIn"
                        >
                            Time in
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            class="h-12 text-base"
                            :disabled="form.processing"
                            @click="clockOut"
                        >
                            Time out
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Today's punches</CardTitle>
                    <CardDescription>Who has timed in or out today.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div v-if="punches.length === 0" class="rounded-md border border-dashed px-3 py-8 text-center text-sm text-muted-foreground">
                        No punches yet today.
                    </div>
                    <ul v-else class="divide-y rounded-md border">
                        <li v-for="punch in punches" :key="punch.id" class="flex items-center justify-between gap-3 px-3 py-3">
                            <div>
                                <p class="text-sm font-medium text-gray-900">{{ punch.name }}</p>
                                <p class="text-xs text-muted-foreground">
                                    {{ punch.employee_number }}
                                    <span v-if="punch.title"> · {{ punch.title }}</span>
                                </p>
                            </div>
                            <p class="shrink-0 text-sm text-gray-700">
                                {{ punch.timed_in_label ?? "—" }}
                                <span class="text-muted-foreground"> → </span>
                                {{ punch.timed_out_label ?? "—" }}
                            </p>
                        </li>
                    </ul>
                </CardContent>
            </Card>
        </div>
    </div>
</template>
